import { Link } from 'react-router-dom'
import type { ProjectSummary } from '@ai-meeting/shared'
import { CalendarIcon, ChevronRightIcon, TagIcon } from '@/components/Icons'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'

interface ProjectCardProps {
  project: ProjectSummary
}

/**
 * One project in the directory grid: what it is called, its key, how many
 * meetings here belong to it, and the way out to Backlog.
 *
 * The key sits where a client name would — it is what this product actually
 * knows about a project, and it is what people quote to each other.
 */
export default function ProjectCard({ project }: ProjectCardProps) {
  const { t } = useUiStrings()
  const meetings =
    project.meetingCount === 1
      ? t.projectsMeetingCountOne
      : fmt(t.projectsMeetingCount, { count: project.meetingCount })

  return (
    <article className="flex flex-col rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-5 py-4 shadow-[var(--shadow-sm)] transition-colors hover:border-[var(--border-hover)]">
      <h3 className="truncate text-sm font-semibold text-[var(--text-primary)]" title={project.name}>
        {project.name}
      </h3>
      <p className="mt-1.5 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
        <TagIcon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />
        <span className="truncate font-semibold">{project.projectKey}</span>
      </p>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-[var(--border-color)] pt-3">
        <span className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <CalendarIcon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />
          <span className="tabular-nums">{meetings}</span>
        </span>
        {/* This app's own screen, not Backlog: the meetings recorded against a
            project are not visible there, and the detail page offers the jump
            out to Backlog for everything that is. */}
        <Link
          to={`/projects/${encodeURIComponent(project.projectKey)}`}
          aria-label={fmt(t.projectsOpenDetail, { name: project.name })}
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:border-[var(--border-hover)] hover:text-[var(--text-primary)]"
        >
          {t.projectsDetails}
          <ChevronRightIcon className="h-3.5 w-3.5" />
        </Link>
      </div>
    </article>
  )
}
